/**
 * Is this photo worth running OCR on?
 *
 * Reading a blurred label produces confident nonsense — the recogniser has no
 * way to say "I could not see this", it just returns its best guess for shapes
 * that were never there. So the cheap checks run first, and a photo that fails
 * them is sent back with one specific instruction rather than a result.
 *
 * Everything here is a single pass over a downsampled greyscale copy, so it
 * costs a few milliseconds against the seconds OCR would have taken.
 */

import type { ImageQuality, QualityProblem, QualityVerdict } from './types';

/** Below this, character strokes are thinner than the recogniser can resolve. */
const MIN_USEFUL_PIXELS = 480 * 480;

export function toGrey(image: ImageData): Float32Array {
  const { data, width, height } = image;
  const out = new Float32Array(width * height);
  for (let i = 0, p = 0; i < data.length; i += 4, p++) {
    // Rec. 601 luma: matches how the eye weights the channels, and how the
    // label was designed to be read.
    out[p] = 0.299 * data[i]! + 0.587 * data[i + 1]! + 0.114 * data[i + 2]!;
  }
  return out;
}

/**
 * Variance of the Laplacian — the standard blur estimate.
 *
 * A sharp edge produces a large second derivative; a blurred one spreads the
 * same intensity change over many pixels and the response collapses. The
 * variance over the whole image is therefore a good proxy for "is anything in
 * focus", and it does not care what the subject is.
 */
export function sharpnessScore(grey: Float32Array, width: number, height: number): number {
  if (width < 3 || height < 3) return 0;
  let sum = 0;
  let sumSq = 0;
  let n = 0;
  for (let y = 1; y < height - 1; y++) {
    for (let x = 1; x < width - 1; x++) {
      const i = y * width + x;
      const lap =
        4 * grey[i]! - grey[i - 1]! - grey[i + 1]! - grey[i - width]! - grey[i + width]!;
      sum += lap;
      sumSq += lap * lap;
      n++;
    }
  }
  if (n === 0) return 0;
  const mean = sum / n;
  const variance = sumSq / n - mean * mean;
  // ~120 is comfortably sharp for printed text at phone resolution; the curve
  // saturates rather than rewarding noise.
  return clamp01(Math.sqrt(Math.max(0, variance)) / 12);
}

export function histogramStats(grey: Float32Array): {
  mean: number;
  stdDev: number;
  clippedHigh: number;
} {
  let sum = 0;
  let sumSq = 0;
  let high = 0;
  for (let i = 0; i < grey.length; i++) {
    const v = grey[i]!;
    sum += v;
    sumSq += v * v;
    if (v > 247) high++;
  }
  const n = grey.length || 1;
  const mean = sum / n;
  return {
    mean,
    stdDev: Math.sqrt(Math.max(0, sumSq / n - mean * mean)),
    clippedHigh: high / n,
  };
}

/**
 * Brightness scored as distance from the middle, not as absolute level.
 *
 * Both failure modes matter: a dark photo loses the thin strokes, and an
 * over-exposed one loses the paper/ink boundary entirely.
 */
function brightnessScore(mean: number): number {
  const ideal = 150;
  return clamp01(1 - Math.abs(mean - ideal) / 105);
}

/** Glare: specular highlights from flash or gloss laminate, which erase text. */
function glareScore(clippedHigh: number): number {
  // A little pure white is just paper. Past ~6% of the frame it is a hotspot.
  return clamp01(1 - Math.max(0, clippedHigh - 0.02) / 0.12);
}

export function assessQuality(
  image: ImageData,
  options: { perspective?: number; labelPixels?: number } = {},
): QualityVerdict {
  const grey = toGrey(image);
  const { mean, stdDev, clippedHigh } = histogramStats(grey);

  const pixels = options.labelPixels ?? image.width * image.height;

  const quality: ImageQuality = {
    resolution: clamp01(pixels / MIN_USEFUL_PIXELS),
    sharpness: sharpnessScore(grey, image.width, image.height),
    brightness: brightnessScore(mean),
    contrast: clamp01(stdDev / 55),
    glare: glareScore(clippedHigh),
    perspective: options.perspective ?? 1,
    overall: 0,
  };

  /*
   * Weakest link, not an average.
   *
   * An average lets four good scores hide one fatal one: a perfectly lit,
   * perfectly square photo that happens to be out of focus is unreadable, and
   * averaging would call it 0.8. The minimum is what actually decides whether
   * OCR can work, with a light average blended in so the number still moves as
   * the user improves things.
   */
  const scores = [
    quality.resolution,
    quality.sharpness,
    quality.brightness,
    quality.contrast,
    quality.glare,
    quality.perspective,
  ];
  const worst = Math.min(...scores);
  const avg = scores.reduce((a, b) => a + b, 0) / scores.length;
  quality.overall = clamp01(worst * 0.7 + avg * 0.3);

  const problems: QualityProblem[] = [];
  if (quality.sharpness < 0.35) problems.push('blurry');
  if (mean < 70) problems.push('dark');
  else if (mean > 215) problems.push('bright');
  if (quality.contrast < 0.3) problems.push('low_contrast');
  if (quality.glare < 0.5) problems.push('glare');
  if (quality.resolution < 0.45) problems.push('too_small');
  if (quality.perspective < 0.55) problems.push('skewed');

  return {
    quality,
    problems,
    // Deliberately permissive: this gate exists to catch photos that are
    // hopeless, not to be fussy. Marginal images still go through, and the
    // per-field confidence downstream is what protects the reader.
    usable: quality.overall >= 0.3 && !problems.includes('blurry'),
    advice: adviceFor(problems),
  };
}

/**
 * One instruction, not a list.
 *
 * Told three things at once, people fix none of them. Problems are ordered by
 * how much they cost the recogniser, and only the first is shown.
 */
export function adviceFor(problems: QualityProblem[]): string | null {
  const order: Array<[QualityProblem, string]> = [
    ['blurry', 'Too blurry — hold the phone still and let it focus.'],
    ['too_small', 'Move closer so the label fills more of the frame.'],
    ['glare', 'Glare on the label — tilt it slightly away from the light.'],
    ['dark', 'Too dark — find more light or move away from your own shadow.'],
    ['bright', 'Over-exposed — move out of direct light.'],
    ['skewed', 'Hold the phone square-on to the label.'],
    ['low_contrast', 'Faint text — try more even lighting.'],
  ];
  for (const [key, message] of order) if (problems.includes(key)) return message;
  return null;
}

export function clamp01(v: number): number {
  return v < 0 ? 0 : v > 1 ? 1 : v;
}
